import { useSetPageTitle } from "src/hooks/useSetPageTitle";
import { AppLayout } from "src/components/layout/app-layout";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import TrendsTab from "./tabs/trends.tab";
import SurveyResultsPage from "../survey-results/survey-results.page";
import OverviewTab from "./tabs/overview.tab";
import { useGetSurveyQuery } from "src/redux/survey/survey.endpoints";
import { useEffect, useMemo } from "react";

const ResultsPage = () => {
  useSetPageTitle('Reports');
  const navigate = useNavigate();
  const { clientId, extSurveyId } = useParams<{ clientId?: string; extSurveyId?: string; shareId?: string }>();
  const [searchParams] = useSearchParams();

  const shareIdFromQuery = searchParams.get('share') ?? undefined;
  const isShare = Boolean(shareIdFromQuery);

  const { data: survey, isLoading: isLoadingSurvey, isFetching: isFetchingSurvey } = useGetSurveyQuery(
    { ext_survey_id: extSurveyId ?? '', share: isShare },
    { skip: !extSurveyId }
  );

  const showTrends = survey?.hasTrends ?? false;
  const isLoadingTrends = isLoadingSurvey || isFetchingSurvey;

  const reportsTabs = useMemo(() => {
    const tabs = [
      { id: 'overview' as const, key: 'ALL', label: 'Overview' },
      { id: 'preview' as const, key: 'NEVER', label: 'Overview (Beta)' },
      ...(showTrends ? [{ id: 'trends' as const, key: 'trends', label: 'Trends' }] : []),
    ];
    return tabs;
  }, [showTrends, isLoadingTrends]);

  const { tab } = useParams<{ tab?: string }>();

  const validTabs = reportsTabs.map(t => t.id);
  const currentTab = validTabs.includes(tab as any) ? (tab as any) : 'overview';

  /** Legacy: `/results/overview/:extSurveyId?surveyGroupId=` → dedicated group route. */
  useEffect(() => {
    const legacyGroupId = searchParams.get('surveyGroupId');
    if (!legacyGroupId || !extSurveyId) {
      return;
    }
    const gs = searchParams.get('groupSurveys');
    const qs = gs ? `?groupSurveys=${encodeURIComponent(gs)}` : '';
    if (clientId) {
      navigate(`/client/${clientId}/results/group/${legacyGroupId}${qs}`, { replace: true });
    } else {
      navigate(`/results/group/${legacyGroupId}${qs}`, { replace: true });
    }
  }, [clientId, extSurveyId, navigate, searchParams]);

  useEffect(() => {
    if (tab === 'trends' && !isLoadingTrends && !showTrends) {
      const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
      if (clientId) {
        navigate(`/client/${clientId}/results/overview/${extSurveyId}${search}`, { replace: true });
      } else {
        navigate(`/results/overview/${extSurveyId}${search}`, { replace: true });
      }
    }
  }, [tab, showTrends, isLoadingTrends, clientId, extSurveyId, navigate, searchParams]);

  const handleTabChange = (tabId: string) => {
    const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
    if (clientId) {
      navigate(`/client/${clientId}/results/${tabId}/${extSurveyId}${search}`);
    } else {
      navigate(`/results/${tabId}/${extSurveyId}${search}`);
    }
  };

  // New layout for org admins and regular users
  return (
    <AppLayout
      activePage="/results"
      navbarProps={{
        showBackButton: false,
        backPath: clientId ? `/client/${clientId}/reports` : '/surveys',
        tabs: reportsTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >
      <div className="flex h-[calc(100vh-8rem)] min-h-0 w-full flex-col">
        {currentTab === 'overview' && <SurveyResultsPage />}
        {currentTab === 'preview' && <OverviewTab />}
        {currentTab === 'trends' && <TrendsTab />}
        {/* {currentTab === 'analysis' && <AnalysisTab />} */}
      </div>
    </AppLayout>
  );
};

export default ResultsPage;